import { action, computed, makeObservable, observable } from 'mobx';

import { BackendEnums } from 'api/codegen';
import { ApiClient } from 'api/codegen/api';

import { getFeatureTargetTierLevel } from './featureDefinitions';

// Picks the price to advertise for a target tier. During the Pricing 2026 transition both the
// old TIERED and the new PER_UNIT row may be public on the same tier level — prefer the new one.
// Never fall back across tiers: buying a lower tier would not unlock the requested feature.
export function selectUpsellPrice(
  prices: Backend.SubscriptionsPriceExpanded[],
  currency: string,
  targetTierLevel: number,
  productType: 'MANAGER' | 'TRACKING' = 'MANAGER',
): Backend.SubscriptionsPriceExpanded | null {
  const productPrices = prices.filter(
    (p) => p.product.internal_type === productType && p.currency === currency && p.tier_level > 0,
  );

  const exactMatches = productPrices.filter((p) => p.tier_level === targetTierLevel);
  return exactMatches.find((p) => p.billing_scheme === 'PER_UNIT') || exactMatches[0] || null;
}

class FeatureUpsellStore {
  @observable isOpen = false;
  @observable feature: BackendEnums.FeaturesEnum | null = null;
  @observable prices: Backend.SubscriptionsPriceExpanded[] | null = null;
  @observable isPriceLoading = false;

  private currency: string | null = null;

  constructor() {
    makeObservable(this);
  }

  // Price for the feature currently shown in the upsell modal.
  @computed get upsellPrice(): Backend.SubscriptionsPriceExpanded | null {
    if (!this.feature) return null;
    const productType = this.feature.startsWith('tracking_') ? 'TRACKING' : 'MANAGER';
    return this.priceForTier(getFeatureTargetTierLevel(this.feature), productType);
  }

  priceForTier(
    tierLevel: number,
    productType: 'MANAGER' | 'TRACKING' = 'MANAGER',
  ): Backend.SubscriptionsPriceExpanded | null {
    if (!this.prices || !this.currency) return null;
    return selectUpsellPrice(this.prices, this.currency, tierLevel, productType);
  }

  @action open(feature: BackendEnums.FeaturesEnum) {
    this.feature = feature;
    this.isOpen = true;
  }

  @action close() {
    this.isOpen = false;
  }

  @action loadPrices = async (api: ApiClient, billingCurrency: string) => {
    if (this.prices || this.isPriceLoading) return;

    this.currency = billingCurrency;
    this.isPriceLoading = true;
    try {
      this.prices = await api.SubscriptionsPriceApi.list();
    } finally {
      this.isPriceLoading = false;
    }
  };
}

const featureUpsellStore = new FeatureUpsellStore();

export default featureUpsellStore;
